/* eslint-disable @next/next/no-img-element */
import { gql } from '@apollo/client';
import React from 'react';
import classnames from 'classnames';
import { When } from 'react-if';

import { useQueryWithLog } from '@lib/hooks/logging-hooks';

import appConfig from '@nerdwallet/app-config';
import { Box } from '@nerdwallet/currency/components/Box';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { Tooltip } from '@nerdwallet/currency/components/Tooltip';
import { Text } from '@nerdwallet/currency/components/Text';
import TUTooltipContent from '@components/tu-tooltip/tu-tooltip';

import styles from './credit-meter.module.scss';
import { capitalizeFirstLetter, getMarkerLocation } from './utils';
import { TuVantageScore3Buckets } from '@generated-gql/generated-types';
import { getLogger } from '@lib/logging';
import { getDateDifference } from '@lib/utils';

export const CREDIT_SCORE_IDENTITY_PROFILE_QUERY = gql`
  query getIdentityProfileQuery {
    tuCreditReportIdentityProfile {
      vantageScore3
      reportPulledAt
      vantageScore3Bucket
    }
  }
`;

const tuLogo = `${appConfig.CDN_URL}/nodeapp/my-nerdwallet/images/tu-logo-blue.png`;

interface CreditScoreMeterProps {
  shouldHighlightActive?: boolean;
  shouldShowCreditScore?: boolean;
  shouldShowMinimalView?: boolean;
}

const logger = getLogger('CreditMeter');

const CreditScoreMeter: React.FC<CreditScoreMeterProps> = ({
  shouldHighlightActive = false,
  shouldShowCreditScore = false,
  shouldShowMinimalView = false,
}) => {
  const { data, error, loading } = useQueryWithLog(
    logger,
    CREDIT_SCORE_IDENTITY_PROFILE_QUERY,
  );

  const identityProfile = data?.tuCreditReportIdentityProfile;

  const creditScoreTier =
    identityProfile?.vantageScore3Bucket ?? TuVantageScore3Buckets.Poor;

  const creditScore = identityProfile?.vantageScore3 ?? 0;

  const lastUpdateString = `Updated: ${getDateDifference(identityProfile?.reportPulledAt)} ago`;

  if (error) {
    return null;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={classnames(styles.meter)}>
      <When condition={!!creditScore}>
        <div
          className={styles.caret}
          style={{
            marginLeft: `calc(${getMarkerLocation(creditScoreTier, creditScore)}% - 6px)`,
          }}
        >
          <Box className={styles.caretLabel}>
            <Text size="small">
              {shouldShowCreditScore
                ? creditScore
                : capitalizeFirstLetter(identityProfile?.vantageScore3Bucket)}
            </Text>
          </Box>
        </div>
      </When>
      <div className={styles.meterGauge}>
        <span
          className={classnames(styles.poor, {
            [styles.understate]:
              shouldHighlightActive &&
              creditScoreTier !== TuVantageScore3Buckets.Poor,
          })}
        />
        <span
          className={classnames(styles.average, {
            [styles.understate]:
              shouldHighlightActive &&
              creditScoreTier !== TuVantageScore3Buckets.Average,
          })}
        />
        <span
          className={classnames(styles.good, {
            [styles.understate]:
              shouldHighlightActive &&
              creditScoreTier !== TuVantageScore3Buckets.Good,
          })}
        />
        <span
          className={classnames(styles.excellent, {
            [styles.understate]:
              shouldHighlightActive &&
              creditScoreTier !== TuVantageScore3Buckets.Excellent,
          })}
        />
      </div>

      <When condition={!shouldShowMinimalView}>
        <div className={styles.bottomContainer}>
          <Text light size="small" className={styles.lastUpdated}>
            {lastUpdateString}
          </Text>
          <Tooltip content={TUTooltipContent()}>
            <img src={tuLogo} className={styles.tuLogo} alt="TransUnion" />
          </Tooltip>
        </div>
      </When>
    </div>
  );
};

export default CreditScoreMeter;
